import type {
  OperationItem,
  OperationItemDetails,
} from '@travel-planner/shared/schemas/operations';
import { uploadthingFileUrl } from '@travel-planner/uploadthing/client';

// Typed readers over the operation-item `details` / `legs` / `attachments`
// JSONB. Shapes verified against live `v_itinerary_item` data: locations live
// at `legs[0].details.startLocation | endLocation`, each carrying
// `formattedAddress`/`title`, `coordinates.{latitude,longitude}`, `timezone`.

export type ItemLocation = {
  // Short place NAME (leg `title`) — what the Planner and the Transport/
  // Experience reports show. Distinct from `address` (full `formattedAddress`,
  // used by the Operations list). See TP-4077.
  name: string | null;
  address: string | null;
  lat: number | null;
  lng: number | null;
  timezone: string | null;
  city: string | null;
};

type RawLocation = {
  formattedAddress?: string | null;
  title?: string | null;
  timezone?: string | null;
  city?: string | null;
  coordinates?: { latitude?: number | null; longitude?: number | null } | null;
};

type RawLeg = {
  details?: {
    startLocation?: RawLocation | null;
    endLocation?: RawLocation | null;
    flightNumber?: string | null;
  } | null;
};

const toLocation = (
  loc: RawLocation | null | undefined
): ItemLocation | null => {
  if (!loc) return null;
  const name = loc.title ?? null;
  const address = loc.formattedAddress ?? loc.title ?? null;
  const lat = loc.coordinates?.latitude ?? null;
  const lng = loc.coordinates?.longitude ?? null;
  if (!address && lat == null && lng == null) return null;
  return {
    name,
    address,
    lat,
    lng,
    timezone: loc.timezone ?? null,
    city: loc.city ?? null,
  };
};

export const getOperationItemDetails = (
  item: Pick<OperationItem, 'details'>
): OperationItemDetails =>
  (item.details && typeof item.details === 'object'
    ? (item.details as OperationItemDetails)
    : {}) as OperationItemDetails;

// A pickup / drop-off the planner stored as a pointer to another item on the
// trip, resolved server-side (`enrichListRowLocationRefs`). Same field names as
// a leg location, so `toLocation` reads either.
const refToLocation = (
  ref: OperationItem['referencedPickup']
): ItemLocation | null =>
  ref
    ? toLocation({
        title: ref.title,
        formattedAddress: ref.formattedAddress,
        city: ref.city,
        timezone: ref.timezone,
        coordinates: { latitude: ref.latitude, longitude: ref.longitude },
      })
    : null;

// Choose between the item's own leg location and the resolved reference.
//
// The item's OWN location wins whenever it has an address: it is what the
// Planner's Location & Time tab edits, what every pickup/drop-off save copies
// the selection into, and what the traveller preview shows. The reference only
// fills a side whose leg has no address (legacy legs holding just
// city/coordinates). Keyed on the ADDRESS, not on null, so an address-less leg
// never reads "Set location" while a reference could name the place; when
// neither has an address the leg is kept, so its coordinates survive.
const preferOwnAddress = (
  legLocation: ItemLocation | null,
  ref: OperationItem['referencedPickup']
): ItemLocation | null => {
  if (legLocation?.address) return legLocation;
  const referenced = refToLocation(ref);
  return referenced?.address ? referenced : legLocation;
};

export const getItemLegLocations = (
  item: Pick<OperationItem, 'legs' | 'referencedPickup' | 'referencedDropoff'>
): { start: ItemLocation | null; end: ItemLocation | null } => {
  const legs = Array.isArray(item.legs) ? (item.legs as RawLeg[]) : [];
  const first = legs[0];
  return {
    start: preferOwnAddress(
      toLocation(first?.details?.startLocation),
      item.referencedPickup
    ),
    end: preferOwnAddress(
      toLocation(first?.details?.endLocation),
      item.referencedDropoff
    ),
  };
};

// Legacy rows store the leg `flightNumber` as a JSON number.
const trimmedStr = (v: unknown): string | null => {
  const raw = typeof v === 'number' && Number.isFinite(v) ? String(v) : v;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
};

// Flight number for a transport item, matching the report's read chain: item
// `details.ticketNumber`/`flightNumber`, then the first leg's
// `details.ticketNumber`/`flightNumber`. The Planner Location tab writes the
// flight number to the leg's `ticketNumber`, so reading only `flightNumber`
// missed it (TP-4059). A transport sub-item with none of its own falls back
// to its parent's (a transfer under a flight — legacy parity); other sub-items
// (tickets, extras) never inherit it.
export const getItemFlightNumber = (
  item: Pick<
    OperationItem,
    'details' | 'legs' | 'contentCategory' | 'parentFlightNumber'
  >
): string | null => {
  const details = getOperationItemDetails(item) as Record<string, unknown>;
  const fromDetails =
    trimmedStr(details.ticketNumber) ?? trimmedStr(details.flightNumber);
  if (fromDetails) return fromDetails;
  const legs = Array.isArray(item.legs) ? (item.legs as RawLeg[]) : [];
  const legDetails = legs[0]?.details as Record<string, unknown> | undefined;
  return (
    trimmedStr(legDetails?.ticketNumber) ??
    trimmedStr(legDetails?.flightNumber) ??
    (item.contentCategory === 'Transportation'
      ? trimmedStr(item.parentFlightNumber)
      : null)
  );
};

export type ItemAttachment = {
  type?: string | null;
  url?: string | null;
  name?: string | null;
  key?: string | null;
  [k: string]: unknown;
};

type RawAttachment = {
  type?: string | null;
  url?: string | null;
  name?: string | null;
  key?: string | null;
  fileKey?: string | null;
  fileNameBase?: string | null;
  file?: { name?: string | null } | null;
  /** When explicitly `false`, hide on Operations (TCO-OPS02-2805). */
  showExternally?: boolean | null;
  [k: string]: unknown;
};

// Last path segment of a URL, when it reads like a filename (has an extension).
const basenameFromUrl = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  try {
    const last = new URL(raw).pathname.split('/').filter(Boolean).pop();
    if (!last) return null;
    const decoded = decodeURIComponent(last);
    return decoded.includes('.') ? decoded : null;
  } catch {
    return null;
  }
};

// Document attachments on a ticket, normalised to a single `{ name, key, url }`
// shape. Attachments arrive in several shapes over the shared DB: the legacy /
// subitem-form shape (`{ file: { name }, fileKey }`), the operations
// upload-dialog shape (`{ name, key }`), and the item-edit Attachments-tab
// shape (`{ fileNameBase, fileKey }`, no `name`/`file.name`). Resolve the name
// across all of them (falling back to the URL basename) so a real filename
// shows instead of the "Ticket" placeholder. Resolve the href from the stored
// `url` else `fileKey` → utfs (legacy parity — a fileKey-only row still links).
// DOCUMENT-only (Operations is internal; images live in the preview gallery).
// Also drop `showExternally === false` (TCO-OPS02-2805) — missing/undefined
// means "show" so legacy rows without the flag still appear.
export const getItemDocumentAttachments = (
  item: Pick<OperationItem, 'attachments'>
): ItemAttachment[] => {
  const all = Array.isArray(item.attachments)
    ? (item.attachments as RawAttachment[])
    : [];
  return all
    .filter((a) => (a?.type ?? '').toUpperCase() === 'DOCUMENT')
    .filter((a) => a?.showExternally !== false)
    .map((a): ItemAttachment => {
      const key = a.key ?? a.fileKey ?? null;
      const url = a.url || (key ? uploadthingFileUrl(key) : null);
      const name =
        a.name ?? a.file?.name ?? a.fileNameBase ?? basenameFromUrl(url);
      return { ...a, name, key, url };
    });
};

// Replace one leg location (start | end) with a picked location, returning a
// new `legs` array suitable for `updateItem({ legs })`.
export const withUpdatedLegLocation = (
  item: Pick<OperationItem, 'legs'>,
  which: 'startLocation' | 'endLocation',
  location: {
    // Short place name → leg `title` (drives the driver/guide WhatsApp
    // message, which must stay short — TP-4077).
    title: string;
    // Full formatted address → leg `formattedAddress` (Operations list).
    // Falls back to the short title when not supplied.
    address?: string | null;
    lat: number;
    lng: number;
    timezone?: string | null;
  }
): unknown[] => {
  const legs = Array.isArray(item.legs)
    ? (item.legs as RawLeg[]).map((l) => ({ ...l }))
    : [];
  const first = (legs[0] ?? {}) as RawLeg & Record<string, unknown>;
  const details = { ...(first.details ?? {}) } as Record<string, unknown>;
  details[which] = {
    ...((details[which] as Record<string, unknown>) ?? {}),
    formattedAddress: location.address ?? location.title,
    title: location.title,
    timezone: location.timezone ?? null,
    coordinates: { latitude: location.lat, longitude: location.lng },
  };
  legs[0] = { ...first, details } as RawLeg;
  return legs;
};
